import { FormQuestionState, FormSectionState } from '@pixacare/forms';
import { FormBridge } from '@pixacare/forms-angular';

/**
 * Merges sections across multiple {@link FormBridge} instances into a single
 * deduplicated list of visible sections.
 *
 * A section is included if it is visible (`!isHidden`) in at least one bridge.
 * Its state is taken from the first bridge that exposes it as visible. Order
 * follows the first bridge's section list so conditional sections keep their
 * original form position regardless of which bridge first reveals them.
 *
 * @param bridges Bridges to merge, in priority order.
 * @returns Visible sections, deduplicated by id and ordered by the first bridge.
 */
export const mergeVisibleSections = (bridges: FormBridge[]): FormSectionState[] => {
  if (bridges.length === 0) {
    return [];
  }
  const visibilityMap = new Map<string, FormSectionState>();
  for (const bridge of bridges) {
    for (const section of bridge.sections()) {
      if (!section.isHidden && !visibilityMap.has(section.id)) {
        visibilityMap.set(section.id, section);
      }
    }
  }
  return bridges[0]
    .sections()
    .filter((s) => visibilityMap.has(s.id))
    .map((s) => visibilityMap.get(s.id)!);
};

/**
 * Merges the questions of a given section across multiple {@link FormBridge}
 * instances into a single deduplicated list of visible questions.
 *
 * A question is included if it is visible in at least one bridge. Its state is
 * taken from the first bridge that exposes it as visible. Order follows the
 * first bridge that reveals each question id (Map preserves insertion order),
 * so questions keep their original section position.
 *
 * @param bridges Bridges to merge, in priority order.
 * @param sectionId Id of the section whose questions should be merged.
 * @returns Visible question states, deduplicated by id and ordered by first appearance.
 */
export const mergeVisibleQuestions = (bridges: FormBridge[], sectionId: string): FormQuestionState[] => {
  const visibilityMap = new Map<string, FormQuestionState>();
  for (const bridge of bridges) {
    const section = bridge.sections().find((s) => s.id === sectionId);
    if (!section) {
      continue;
    }
    const questions = bridge.questions();
    for (const qid of section.questions) {
      if (visibilityMap.has(qid)) {
        continue;
      }
      const state = questions[qid];
      if (!state || state.isHidden) {
        continue;
      }
      visibilityMap.set(qid, state);
    }
  }
  return Array.from(visibilityMap.values());
};
